/* "Convert a video" drawer on a Micro Drama season's Episodes page.
   Pick a long video from the Media Library → choose how a horizontal frame
   fills a vertical screen and how long each episode is → start. The server
   cuts at scene changes, reframes to 9:16 and adds the parts as episodes. */

import React, { useEffect, useMemo, useState } from "react";
import um from "../../userManagement/userManagement.module.css";
import s from "./MicroDrama.module.css";
import MediaLibraryModal from "../../../component/mediaLibrary/MediaLibraryModal";
import { showToast } from "../../../utils/Toastify/Toastify";
import {
  LENGTH_OPTIONS,
  DEFAULT_LENGTH,
  describeSource,
  describeSeriesSource,
  searchSeries,
  startConversion,
  startSeriesConversion,
  apiErrorMessage,
  formatLength,
  estimateEpisodes,
} from "./microDramaService";

const ORIENTATION_LABEL = { horizontal: "Horizontal", vertical: "Vertical", square: "Square", unknown: "Unknown shape" };

/** Tiny phone mock showing what a reframe mode does to a wide picture. */
function FramePreview({ mode }) {
  return (
    <span className={`${s.phone} ${mode === "crop" ? s.phoneCrop : s.phoneBlur}`} aria-hidden="true">
      {mode === "blur" && <span className={s.phoneBg} />}
      <span className={s.phoneScene}>
        <span className={s.sceneSun} />
        <span className={s.scenePerson} />
        <span className={`${s.scenePerson} ${s.scenePerson2}`} />
      </span>
    </span>
  );
}

const REFRAME_OPTIONS = [
  {
    value: "blur",
    title: "Blurred background",
    text: "The whole picture stays visible, with a soft blur above and below.",
    tag: "Recommended",
  },
  {
    value: "crop",
    title: "Fill the screen",
    text: "Fills the phone, but cuts off the left and right sides.",
  },
];

/** Search-as-you-type list of the CMS's series (minus the one we're converting into). */
function SeriesPicker({ excludeId, onPick, disabled }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      searchSeries(q.trim())
        .then((r) => alive && setRows(r.filter((x) => String(x.id) !== String(excludeId))))
        .catch(() => alive && setRows([]));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, excludeId]);
  return (
    <div className={s.seriesPicker}>
      <input
        className={um.input}
        placeholder="Search your series…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="Search series"
        disabled={disabled}
      />
      <div className={s.seriesList} role="listbox" aria-label="Series">
        {rows === null && <div className={s.muted}>Loading…</div>}
        {rows && !rows.length && <div className={s.muted}>No series found.</div>}
        {(rows || []).map((r) => (
          <button key={r.id} type="button" role="option" aria-selected="false" className={s.seriesRow} onClick={() => onPick(r)} disabled={disabled}>
            <span className={s.seriesThumb} style={r.image ? { backgroundImage: `url(${r.image})` } : undefined} aria-hidden="true" />
            <span className={s.seriesName}>{r.name}</span>
            {r.format && <span className={s.muted}>{r.format}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ConvertDrawer({ seriesId, seasonId, seriesName, onClose, onStarted }) {
  const [mode, setMode] = useState("video"); // video | series
  const [sourceSeries, setSourceSeries] = useState(null); // { id, name }
  const [seriesInfo, setSeriesInfo] = useState(null);
  const [seriesState, setSeriesState] = useState("idle");
  const [seriesError, setSeriesError] = useState("");
  const [video, setVideo] = useState(null); // { id, title }
  const [picking, setPicking] = useState(false);
  const [info, setInfo] = useState(null);
  const [infoState, setInfoState] = useState("idle"); // idle | loading | ok | error
  const [infoError, setInfoError] = useState("");
  const [reframe, setReframe] = useState("blur");
  const [length, setLength] = useState(DEFAULT_LENGTH);
  const [prefix, setPrefix] = useState("");
  const [prefixTouched, setPrefixTouched] = useState(false);
  const [publish, setPublish] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!video?.id) return undefined;
    let alive = true;
    setInfoState("loading");
    setInfo(null);
    setInfoError("");
    describeSource(video.id)
      .then((d) => {
        if (!alive) return;
        setInfo(d);
        setInfoState("ok");
      })
      .catch((err) => {
        if (!alive) return;
        setInfoError(apiErrorMessage(err, "Couldn't read this video"));
        setInfoState("error");
      });
    return () => {
      alive = false;
    };
  }, [video?.id]);

  // A whole series: episode count + estimate come from the server (summed per episode).
  useEffect(() => {
    if (!sourceSeries?.id) return undefined;
    let alive = true;
    setSeriesState("loading");
    setSeriesError("");
    describeSeriesSource(sourceSeries.id, length)
      .then((d) => {
        if (!alive) return;
        setSeriesInfo(d);
        setSeriesState("ok");
      })
      .catch((err) => {
        if (!alive) return;
        setSeriesError(apiErrorMessage(err, "Couldn't read this series"));
        setSeriesState("error");
      });
    return () => {
      alive = false;
    };
  }, [sourceSeries?.id, length]);

  const sourceName = mode === "series" ? sourceSeries?.name : video?.title;
  useEffect(() => {
    if (!prefixTouched) setPrefix(sourceName || seriesName || "");
  }, [sourceName, seriesName, prefixTouched]);

  const videoEstimate = useMemo(() => estimateEpisodes(info?.duration_sec, length), [info, length]);
  const estimate = mode === "series" ? seriesInfo?.estimated_parts || 0 : videoEstimate;
  const vertical = mode === "video" && info?.orientation === "vertical";
  const seriesOk = seriesState === "ok" && seriesInfo?.episodes > 0 && !seriesInfo?.too_many;
  const canStart = !busy && (mode === "series" ? Boolean(sourceSeries?.id) && seriesOk : Boolean(video?.id) && infoState === "ok");
  const namePreview = `${prefix.trim() ? `${prefix.trim()} · ` : ""}Part 1`;

  const start = async () => {
    if (!canStart) return;
    setBusy(true);
    try {
      const common = {
        series_id: Number(seriesId),
        season_id: Number(seasonId),
        reframe,
        target_seconds: length,
        name_prefix: prefix.trim(),
        publish,
      };
      const result =
        mode === "series"
          ? await startSeriesConversion({ ...common, source_series_id: sourceSeries.id })
          : await startConversion({ ...common, video_id: video.id });
      showToast(
        mode === "series"
          ? `Converting ${seriesInfo?.episodes || ""} episodes, one after another. Episodes appear here as each one is ready.`.replace("  ", " ")
          : "Conversion started. Episodes appear here as each one is ready.",
        "success"
      );
      onStarted?.(result);
    } catch (err) {
      showToast(apiErrorMessage(err, "Couldn't start the conversion"), "error");
      setBusy(false);
    }
  };

  return (
    <div className={um.modalOverlay} onClick={busy ? undefined : onClose}>
      <aside className={`${um.drawer} ${s.drawer}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Convert a video into episodes">
        <div className={um.drawerHeader}>
          <div>
            <div className={um.drawerTitle}>Convert a video into episodes</div>
            <div className={um.drawerSub}>
              A movie, a long episode or a whole series is cut into short vertical episodes and added to this season.
            </div>
          </div>
        </div>

        <div className={`${um.drawerBody} ${s.body}`}>
          {/* 1. Video */}
          <section className={s.step}>
            <div className={s.stepHead}>
              <span className={s.stepNum}>1</span>
              <span className={s.stepTitle}>What to convert</span>
            </div>
            <div className={s.segment} role="radiogroup" aria-label="What to convert">
              {[
                ["video", "One video"],
                ["series", "A whole series"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={mode === v}
                  className={`${s.segmentBtn} ${mode === v ? s.segmentOn : ""}`}
                  onClick={() => setMode(v)}
                  disabled={busy}
                >
                  {label}
                </button>
              ))}
            </div>
            {mode === "series" ? (
              !sourceSeries ? (
                <SeriesPicker excludeId={seriesId} onPick={(r) => setSourceSeries({ id: r.id, name: r.name })} disabled={busy} />
              ) : (
                <div className={s.sourceCard} data-testid="md-source-series">
                  <div className={s.sourceMain}>
                    <div className={s.sourceTitle}>{sourceSeries.name}</div>
                    {seriesState === "loading" && <div className={s.muted}>Counting episodes…</div>}
                    {seriesState === "ok" && seriesInfo && (
                      <div className={s.muted}>
                        {seriesInfo.episodes} episode{seriesInfo.episodes === 1 ? "" : "s"}
                        {seriesInfo.duration_sec ? ` · ${formatLength(seriesInfo.duration_sec)} in total` : ""} · converted in order, one after another
                      </div>
                    )}
                    {seriesState === "ok" && seriesInfo && !seriesInfo.episodes && <div className={um.errorText}>This series has no episodes with a video yet.</div>}
                    {seriesState === "ok" && seriesInfo?.too_many && <div className={um.errorText}>That's more than 200 episodes — convert its videos one by one instead.</div>}
                    {seriesState === "error" && <div className={um.errorText}>{seriesError}</div>}
                  </div>
                  <button type="button" className={um.btnGhost} onClick={() => setSourceSeries(null)} disabled={busy}>
                    Change
                  </button>
                </div>
              )
            ) : !video ? (
              <button type="button" className={s.pickBox} onClick={() => setPicking(true)}>
                <span className={s.pickIcon} aria-hidden="true">▶</span>
                <span>
                  <strong>Choose from Media Library</strong>
                  <span className={s.muted}>Any movie or episode you've uploaded</span>
                </span>
              </button>
            ) : (
              <div className={s.sourceCard} data-testid="md-source">
                <div className={s.sourceMain}>
                  <div className={s.sourceTitle}>{video.title || `Video #${video.id}`}</div>
                  {infoState === "loading" && <div className={s.muted}>Checking the video…</div>}
                  {infoState === "ok" && info && (
                    <div className={s.muted}>
                      {formatLength(info.duration_sec)} · {ORIENTATION_LABEL[info.orientation] || info.orientation}
                      {info.width && info.height ? ` ${info.width}×${info.height}` : ""}
                      {info.has_audio === false ? " · no sound" : ""}
                    </div>
                  )}
                  {infoState === "error" && <div className={um.errorText}>{infoError}</div>}
                </div>
                <button type="button" className={um.btnGhost} onClick={() => setPicking(true)} disabled={busy}>
                  Change
                </button>
              </div>
            )}
          </section>

          {/* 2. Reframe */}
          <section className={s.step}>
            <div className={s.stepHead}>
              <span className={s.stepNum}>2</span>
              <span className={s.stepTitle}>Fit to a vertical screen</span>
            </div>
            {mode === "series" && <div className={s.muted}>Episodes that are already vertical are only split.</div>}
            {vertical ? (
              <div className={s.note}>This video is already vertical — it will only be split into episodes.</div>
            ) : (
              <div className={s.options} role="radiogroup" aria-label="Fit to a vertical screen">
                {REFRAME_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={reframe === o.value}
                    className={`${s.option} ${reframe === o.value ? s.optionOn : ""}`}
                    onClick={() => setReframe(o.value)}
                    disabled={busy}
                  >
                    <FramePreview mode={o.value} />
                    <span className={s.optionText}>
                      <span className={s.optionTitle}>
                        {o.title}
                        {o.tag && <span className={s.tag}>{o.tag}</span>}
                      </span>
                      <span className={s.muted}>{o.text}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* 3. Length */}
          <section className={s.step}>
            <div className={s.stepHead}>
              <span className={s.stepNum}>3</span>
              <span className={s.stepTitle}>Episode length</span>
            </div>
            <div className={s.chips} role="radiogroup" aria-label="Episode length">
              {LENGTH_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={length === o.value}
                  className={`${s.chip} ${length === o.value ? s.chipOn : ""}`}
                  onClick={() => setLength(o.value)}
                  disabled={busy}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className={s.muted}>
              Each cut moves to the nearest scene change, so no episode ends mid-shot.
              {estimate > 0 && (
                <>
                  {" "}
                  <strong className={s.estimate} data-testid="md-estimate">
                    ≈ {estimate} episode{estimate === 1 ? "" : "s"}
                  </strong>
                </>
              )}
            </div>
          </section>

          {/* 4. Names + publishing */}
          <section className={s.step}>
            <div className={s.stepHead}>
              <span className={s.stepNum}>4</span>
              <span className={s.stepTitle}>Episodes</span>
            </div>
            <label className={um.field}>
              <span className={um.label}>Episode name</span>
              <input
                className={um.input}
                value={prefix}
                maxLength={150}
                onChange={(e) => {
                  setPrefix(e.target.value);
                  setPrefixTouched(true);
                }}
                aria-label="Episode name"
                disabled={busy}
              />
              <span className={um.hint}>Shown as “{namePreview}”, “Part 2”… You can rename any episode later.</span>
            </label>
            <label className={s.switchRow}>
              <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} disabled={busy} aria-label="Publish episodes when ready" />
              <span>
                <span className={s.optionTitle}>Publish episodes when ready</span>
                <span className={s.muted}>
                  {publish ? "Viewers see each episode as soon as it's ready." : "Episodes are added as drafts so you can review them first."}
                </span>
              </span>
            </label>
          </section>
        </div>

        <div className={um.drawerFooter}>
          <button type="button" className={um.btnGhost} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={um.btnPrimary} onClick={start} disabled={!canStart}>
            {busy ? "Starting…" : "Start converting"}
          </button>
        </div>

        {picking && (
          <MediaLibraryModal
            isOpen
            setIsOpen={(next) => {
              if (!next) setPicking(false);
            }}
            mode="modal"
            showCheckbox
            type="video"
            handleVideovalue={(item) => {
              if (!item?.id) return;
              setVideo({ id: item.id, title: item.title || item.name || "" });
              setPicking(false);
            }}
          />
        )}
      </aside>
    </div>
  );
}
